import React, { useState, useEffect } from 'react';
import Header from '../../main-page/Header';
import ProfileForm from './ProfileForm';
import UserPreferences from './UserPreferences';
import UserPrivacy from './UserPrivacy';
import UserSecurity from './UserSecurity';
import UserActivity from './UserActivity';
import AdminPanel from './AdminPanel';
import MyContent from './MyContent';
import { authService } from '../../../services/api';

const ProfilePage = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };


  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'my-content', label: 'My content', icon: '📋' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' },
    { id: 'privacy', label: 'Privacy', icon: '🔒' },
    { id: 'security', label: 'Security', icon: '🛡️' },
    { id: 'activity', label: 'Activity', icon: '📊' },
  ];

  // Add admin tab if user has admin privileges
  if (user && (user.role === 'admin' || user.role === 'moderator')) {
    tabs.push({ id: 'admin', label: 'Admin', icon: '👑' });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
        <Header onNavigate={onNavigate} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileForm onNavigate={onNavigate} onUserUpdate={loadUserData} />;
      case 'my-content':
        return <MyContent />;
      case 'preferences':
        return <UserPreferences onNavigate={onNavigate} />;
      case 'privacy':
        return <UserPrivacy onNavigate={onNavigate} />;
      case 'security':
        return <UserSecurity onNavigate={onNavigate} />;
      case 'activity':
        return <UserActivity onNavigate={onNavigate} />;
      case 'admin':
        return <AdminPanel onNavigate={onNavigate} />;
      default:
        return <ProfileForm onNavigate={onNavigate} onUserUpdate={loadUserData} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      <Header onNavigate={onNavigate} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Your Profile</h1>
              <p className="mt-2 text-gray-600">Manage your account information and settings</p>
            </div>
            
            {user && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 font-medium">{user.full_name || user.email}</span>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {user.role}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-1 sm:space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-2 sm:px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2 text-base">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-96">
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
